var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import { DayPicker } from 'react-day-picker';
import { nl } from 'react-day-picker/locale';
import { useDetectOutsideClick } from '../../../../detect-outside-click';
import { ISO_DATE_FORMAT, formatDate, getDisabledDays, safeParseDate } from '../helpers';
import { DateInputContainer } from '../styles';
import { DateInputField } from './DateInputField';
import { Dropdown } from './Dropdown';
export const Datepicker = forwardRef(function DatePickerWithRef(_a, ref) {
    var { value: inputValue = '', reference, name, min, max, placeholder = 'dd-mm-jjjj', disabledDays = [], disabledDaysOfWeek = [], onChange, 
    /**
     * onBlur will be provided by react-hook-form register function but is not needed for this implementation.
     */
    onBlur: _onBlur, withoutInputField = false, defaultCalenderDate } = _a, props = __rest(_a, ["value", "reference", "name", "min", "max", "placeholder", "disabledDays", "disabledDaysOfWeek", "onChange", "onBlur", "withoutInputField", "defaultCalenderDate"]);
    /**
     * This ref is needed to keep track of the value that is passed to the component from the outside. Somehow when in
     * strict mode the useEffect is triggered while the value is not changed. This is a workaround to prevent that.
     */
    const valueFromProp = useRef(inputValue);
    /**
     * This state is needed to keep the value of the input field in sync with the date picker.
     */
    const [value, setValue] = useState(inputValue);
    /**
     * This ref is needed to keep track of the input field. It is the element where the onChange event is added to.
     */
    const inputRef = useRef(null);
    const isTouched = useRef(false);
    const dayPickerRef = useRef(null);
    const [showDayPicker, setShowDayPicker] = useState(withoutInputField);
    const currentDate = safeParseDate(value, ISO_DATE_FORMAT);
    const minDate = safeParseDate(min, ISO_DATE_FORMAT);
    const maxDate = safeParseDate(max, ISO_DATE_FORMAT);
    const defaultMonth = safeParseDate(defaultCalenderDate, ISO_DATE_FORMAT);
    /**
     * When this component is controlled it can be that the value is changed from the outside.
     */
    useEffect(() => {
        if (valueFromProp.current !== inputValue) {
            valueFromProp.current = inputValue;
            setValue(inputValue);
        }
    }, [inputValue]);
    useEffect(() => {
        const element = inputRef.current;
        /*
         * This handler is added to a DOM element, so it doesn't get the React ChangeEvent type. But a normal Event type. By
         * setting it to unknown, we can cast it to a ChangeEvent<HTMLInputElement>.
         */
        const handler = (event) => {
            if (onChange === undefined) {
                return;
            }
            onChange(event);
        };
        if (element !== null) {
            element.addEventListener('change', handler);
        }
        return () => {
            if (element !== null) {
                element.removeEventListener('change', handler);
            }
        };
    }, [onChange]);
    useDetectOutsideClick({
        ref: dayPickerRef,
        onClickOutside: () => {
            if (withoutInputField) {
                return;
            }
            setShowDayPicker(false);
        },
    });
    /**
     * When the value is changed an onChange event should be triggered on the input field.
     */
    useEffect(() => {
        const element = inputRef.current;
        if (element === null) {
            return;
        }
        if (!isTouched.current) {
            return;
        }
        const event = new Event('change');
        element.dispatchEvent(event);
    }, [value]);
    /**
     * useImperativeHandle is used for two reasons:
     * 1. to share the reference as a forwardRef with its parent component.
     * 2. to "listen" to changes from the outside, when the value is changed from the outside by using the forwarded ref.
     *
     * When the value is changed from the outside, the value of the property is set, not the value. The setter sets the value
     * also on the value attribute. This way the value is also 'visible' in the DOM.
     */
    useImperativeHandle(ref, () => {
        const element = inputRef.current;
        Object.defineProperty(element, 'value', {
            configurable: true,
            get: () => {
                var _a;
                return (_a = element.getAttribute('value')) !== null && _a !== void 0 ? _a : '';
            },
            set: (newValue) => {
                element.setAttribute('value', newValue);
                setValue(newValue);
            },
        });
        Object.defineProperty(element, 'defaultValue', {
            configurable: true,
            set: (newValue) => {
                setValue(newValue);
            },
        });
        return element;
    }, []);
    const handleDayPickerSelect = (date) => {
        if (date === undefined) {
            return;
        }
        if (!withoutInputField) {
            setShowDayPicker(false);
        }
        isTouched.current = true;
        setValue(formatDate(date, ISO_DATE_FORMAT));
    };
    const handleInput = (date) => {
        isTouched.current = true;
        setValue(date);
    };
    return (_jsxs(DateInputContainer, { "$isStandalone": withoutInputField, children: [_jsx("input", { type: "hidden", name: name, value: value === undefined ? '' : value, ref: inputRef }), !withoutInputField && (_jsx(DateInputField, Object.assign({ reference: reference, name: name, changeHandler: handleInput, value: value, placeholder: placeholder, showDayPicker: () => {
                    setShowDayPicker(true);
                } }, props))), _jsx("span", { ref: dayPickerRef, children: showDayPicker && (_jsx(DayPicker, { mode: "single", locale: nl, captionLayout: "dropdown", selected: currentDate, onSelect: handleDayPickerSelect, defaultMonth: currentDate !== null && currentDate !== void 0 ? currentDate : defaultMonth, disabled: getDisabledDays(disabledDays, disabledDaysOfWeek, minDate, maxDate), components: {
                        Dropdown: Dropdown,
                    } })) })] }));
});
